import { Amplify } from "aws-amplify";
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
} from "@aws-amplify/auth";
import { cognitoUserPoolsTokenProvider } from "@aws-amplify/auth/cognito";
import { sharedInMemoryStorage } from "@aws-amplify/core";
import { useCallback, useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// SECURITY: In-memory token storage (CRIT-1)
// Tokens never touch localStorage/sessionStorage - eliminates XSS exfiltration.
// Amplify's sharedInMemoryStorage is the built-in equivalent of the previous
// custom Map adapter - same security guarantee, actively maintained.
// Trade-off: users re-authenticate on page refresh (correct for B2B SaaS).
// AWS recommendation: https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-security-best-practices.html
// ---------------------------------------------------------------------------

// Configure Amplify once at module level (replaces createUserPool / getPool).
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID as string,
      userPoolClientId: import.meta.env.VITE_COGNITO_APP_CLIENT_ID as string,
    },
  },
});

// Swap Amplify's default localStorage adapter for the built-in in-memory store.
cognitoUserPoolsTokenProvider.setKeyValueStorage(sharedInMemoryStorage);

// ---------------------------------------------------------------------------
// Module-level cache for the ID token.
// sharedInMemoryStorage.getItem() is async, but getIdToken() must remain sync
// (AgentCore callers do not await it). We shadow the stored value here and
// update it whenever signIn / getCurrentSession runs.
// ---------------------------------------------------------------------------
let _cachedIdToken: string | null = null;

const ID_TOKEN_KEY = "__novasynth_id_token__";

async function _storeIdToken(idJwt: string): Promise<void> {
  _cachedIdToken = idJwt;
  await sharedInMemoryStorage.setItem(ID_TOKEN_KEY, idJwt);
}

async function _clearIdToken(): Promise<void> {
  _cachedIdToken = null;
  await sharedInMemoryStorage.removeItem(ID_TOKEN_KEY);
}

// ---------------------------------------------------------------------------
// Core auth operations
// ---------------------------------------------------------------------------

// SECURITY: Returns Access Token, not ID Token (CRIT-2).
// Access token is the correct bearer credential for the app's own backend APIs.
// ID token (contains PII) is stored separately for AgentCore authentication only,
// because AgentCore's allowedAudience validates the aud claim present only in ID tokens.
// https://docs.aws.amazon.com/cognito/latest/developerguide/amazon-cognito-user-pools-using-tokens-with-identity-providers.html
export async function signIn(email: string, password: string): Promise<string> {
  const result = await amplifySignIn({ username: email, password });

  if (result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
    throw new Error("NEW_PASSWORD_REQUIRED: Account requires a password reset before first use.");
  }

  const session = await fetchAuthSession();
  const accessToken = session.tokens?.accessToken;
  const idToken = session.tokens?.idToken;

  if (!accessToken || !idToken) {
    throw new Error("Authentication succeeded but tokens were not returned.");
  }

  await _storeIdToken(idToken.toString());
  return accessToken.toString();  // access token, not ID token
}

export async function signOut(): Promise<void> {
  try {
    await amplifySignOut({ global: true });
  } catch {
    await amplifySignOut();
  }
  await _clearIdToken();
}

// Returns the current valid Access Token, refreshing silently if expired.
// Also refreshes the stored ID token as a side-effect (used by AgentCore).
// fetchAuthSession() automatically uses the refresh token when the session is near expiry.
export async function getCurrentSession(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken;
    const idToken = session.tokens?.idToken;

    if (!accessToken || !idToken) return null;

    await _storeIdToken(idToken.toString());
    return accessToken.toString();  // access token
  } catch {
    return null;
  }
}

// Returns the Cognito ID token for AgentCore authentication.
// AgentCore's allowedAudience validates the aud claim, present only in ID tokens (not access tokens).
// Synchronous - reads from the module-level cache updated by signIn / getCurrentSession.
export function getIdToken(): string | null {
  return _cachedIdToken;
}

export async function signUp(email: string, password: string): Promise<void> {
  await amplifySignUp({
    username: email,
    password,
    options: { userAttributes: { email } },
  });
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  await amplifyConfirmSignUp({ username: email, confirmationCode: code });
}

export async function resendConfirmationCode(email: string): Promise<void> {
  await resendSignUpCode({ username: email });
}

export async function forgotPassword(email: string): Promise<void> {
  await resetPassword({ username: email });
}

export async function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

export interface AuthState {
  accessToken: string | null;
  idToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  needsVerification: boolean;
  needsPasswordReset: boolean;
  pendingEmail: string | null;
}

export interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  register: (email: string, password: string) => Promise<void>;
  verify: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
}

export type UseAuthReturn = AuthState & AuthActions;

export function useAuth(): UseAuthReturn {
  const [state, setState] = useState<AuthState>({
    accessToken: null,
    idToken: null,
    isLoading: true,
    isAuthenticated: false,
    error: null,
    needsVerification: false,
    needsPasswordReset: false,
    pendingEmail: null,
  });

  const tokenRef = useRef<string | null>(null);
  tokenRef.current = state.accessToken;

  // SECURITY (MED-1): Password held in memory only for the OTP verification window.
  // It is zeroed out immediately when consumed (before any async work), not after callbacks.
  // Never persisted to any storage tier.
  const pendingPasswordRef = useRef<string | null>(null);

  // On mount: try to restore session (in-memory - only survives within same page lifecycle)
  useEffect(() => {
    let cancelled = false;
    getCurrentSession()
      .then((token) => {
        if (!cancelled) {
          setState({
            accessToken: token,
            idToken: token ? getIdToken() : null,
            isLoading: false,
            isAuthenticated: token !== null,
            error: null,
            needsVerification: false,
            needsPasswordReset: false,
            pendingEmail: null,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ accessToken: null, idToken: null, isLoading: false, isAuthenticated: false, error: null, needsVerification: false, needsPasswordReset: false, pendingEmail: null });
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Proactive refresh: Cognito access tokens expire in 60 min; sync state at 50 min.
  // fetchAuthSession() automatically uses the refresh token when the session is near expiry.
  useEffect(() => {
    if (!state.isAuthenticated) return;
    const id = setInterval(async () => {
      const fresh = await getCurrentSession();
      if (fresh && fresh !== tokenRef.current) {
        setState((prev) => ({ ...prev, accessToken: fresh, idToken: getIdToken() }));
      } else if (!fresh) {
        // Refresh token expired - force re-login
        setState({ accessToken: null, idToken: null, isLoading: false, isAuthenticated: false, error: null, needsVerification: false, needsPasswordReset: false, pendingEmail: null });
      }
    }, 50 * 60 * 1000);
    return () => clearInterval(id);
  }, [state.isAuthenticated]);

  const login = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const token = await signIn(email, password);
      setState({ accessToken: token, idToken: getIdToken(), isLoading: false, isAuthenticated: true, error: null, needsVerification: false, needsPasswordReset: false, pendingEmail: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Authentication failed";
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    void signOut();
    setState({ accessToken: null, idToken: null, isLoading: false, isAuthenticated: false, error: null, needsVerification: false, needsPasswordReset: false, pendingEmail: null });
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const register = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await signUp(email, password);
      pendingPasswordRef.current = password;
      setState((prev) => ({ ...prev, isLoading: false, needsVerification: true, pendingEmail: email }));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Registration failed";
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
      throw err;
    }
  }, []);

  const verify = useCallback(async (code: string) => {
    const email = state.pendingEmail;
    if (!email) return;

    // SECURITY (MED-1 fix): capture and zero out the password ref IMMEDIATELY,
    // before any async work, to minimize the memory exposure window.
    const capturedPassword = pendingPasswordRef.current;
    pendingPasswordRef.current = null;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await confirmSignUp(email, code);
      if (capturedPassword) {
        const token = await signIn(email, capturedPassword);
        setState({ accessToken: token, idToken: getIdToken(), isLoading: false, isAuthenticated: true, error: null, needsVerification: false, needsPasswordReset: false, pendingEmail: null });
      } else {
        // No cached password (edge case) - redirect to sign-in
        setState((prev) => ({ ...prev, isLoading: false, needsVerification: false, needsPasswordReset: false, pendingEmail: null }));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Verification failed. Check the code and try again.";
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
      throw err;
    }
  }, [state.pendingEmail]);

  const resendCode = useCallback(async () => {
    const email = state.pendingEmail;
    if (!email) return;
    setState((prev) => ({ ...prev, error: null }));
    try {
      await resendConfirmationCode(email);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to resend code";
      setState((prev) => ({ ...prev, error: message }));
    }
  }, [state.pendingEmail]);

  const forgotPasswordAction = useCallback(async (email: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await forgotPassword(email);
      setState((prev) => ({ ...prev, isLoading: false, needsPasswordReset: true, pendingEmail: email }));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to send reset code";
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
      throw err;
    }
  }, []);

  const confirmForgotPasswordAction = useCallback(async (email: string, code: string, newPassword: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await confirmForgotPassword(email, code, newPassword);
      setState((prev) => ({ ...prev, isLoading: false, needsPasswordReset: false, pendingEmail: null }));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Password reset failed. Check the code and try again.";
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
      throw err;
    }
  }, []);

  return {
    ...state,
    login,
    logout,
    clearError,
    register,
    verify,
    resendCode,
    forgotPassword: forgotPasswordAction,
    confirmForgotPassword: confirmForgotPasswordAction,
  };
}
